import { Components, Theme } from '@mui/material';
import type { WatchdutyThemePalette } from '../palette';
import {
  getDenseResponsiveFontSize,
  getResponsiveFontSizeAtScale,
} from '../watchdutyTheme.utils';

export type WatchdutyTheme = Theme & { palette: WatchdutyThemePalette };

const getChipFontSize = (): string => getResponsiveFontSizeAtScale('0.75rem');

export const getMuiChip = (theme: WatchdutyTheme): Components['MuiChip'] => ({
  styleOverrides: {
    root: ({ ownerState }) => ({
      ...(ownerState.color === 'severe' && {
        backgroundColor: theme.palette.severe.main,
        color: theme.palette.common.white,
      }),
      ...(ownerState.color === 'brand' && {
        backgroundColor: theme.palette.brand[500],
        color: theme.palette.warmGrey[950],
      }),
      variants: [
        {
          props: { variant: 'watchduty' },
          style: {
            borderRadius: 4,
            fontWeight: theme.typography.fontWeightBold,
            fontSize: getChipFontSize(),
            fontVariant: 'small-caps',
            textTransform: 'uppercase',
            lineHeight: 1,
            height: 'fit-content',
            maxHeight: 'fit-content',
            width: 'fit-content',
            minWidth: 'fit-content',
            '& span': {
              height: 'fit-content',
              padding: '3px 4px 2px',
              display: 'block',
              whiteSpace: 'nowrap',
            },
          },
        },
        {
          props: { color: 'black' },
          style: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.brand.main,
          },
        },
        {
          props: { variant: 'keyboard-shortcut' },
          style: {
            color: theme.palette.common.white,
            fontFamily: 'monospace',
            borderRadius: '4px',
            backgroundColor: theme.palette.grey[800],
            margin: 0,
            padding: 0,
            '& span': {
              padding: '0 6px',
              margin: 0,
            },
          },
        },
        {
          // Basic chip variant for flood levels and similar badges
          props: { variant: 'watchduty-basic' },
          style: {
            borderRadius: 4,
            fontWeight: theme.typography.fontWeightBold,
            fontSize: getChipFontSize(),
            minWidth: 64,
          },
        },
        {
          // Layer drawer active/total count — dense, truncated (ENG-6571)
          props: { variant: 'watchduty-count' },
          style: {
            borderRadius: 4,
            minWidth: 42,
            maxWidth: 56,
            flexShrink: 0,
            fontWeight: theme.typography.fontWeightBold,
            fontSize: getDenseResponsiveFontSize('0.75rem'),
            textTransform: 'none',
            fontVariant: 'normal',
            lineHeight: 1,
            height: 'auto',
            width: 'fit-content',
            '& .MuiChip-label, & span': {
              fontWeight: 600,
              fontSize: getDenseResponsiveFontSize('0.75rem'),
              fontVariant: 'normal',
              textTransform: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              maxWidth: '100%',
              padding: '3px 4px 2px',
            },
          },
        },
      ],
    }),
  },
});
